import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, LifeBuoy, RefreshCw } from "lucide-react";

const ISSUE_META = {
  delivery_failures: { label: "Delivery", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  failed_payments: { label: "Payments", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  no_stripe_customer: { label: "Stripe", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

const SEV_CLS = {
  high: "text-rose-700",
  medium: "text-amber-700",
  low: "text-slate-600",
};

export default function SupportIssues() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/operations");
      setData({ rows: res.data.supportIssues || [] });
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="support-issues-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Scanning fleet issues…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const highCount = data.rows.filter(r => r.severity === "high").length;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="support-issues">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-rose-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Support / Issues</h3>
          <span className="text-xs text-slate-500">{data.rows.length} open · {highCount} high</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="support-issues-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="support-issues-empty">No issues across the fleet. All systems green.</div>
      ) : (
        <div className="space-y-1.5" data-testid="support-issues-list">
          {data.rows.slice(0, 6).map((r) => {
            const meta = ISSUE_META[r.issueType] || { label: r.issueType, cls: "bg-slate-50 text-slate-700 border-slate-200" };
            return (
              <div key={r.organisationId + r.issueType} className="p-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`support-issue-${r.organisationId}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`border text-[10px] px-1.5 py-0 ${meta.cls}`}>{meta.label}</Badge>
                  <span className="text-sm font-medium text-slate-900 truncate flex-1">{r.organisation}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${SEV_CLS[r.severity] || SEV_CLS.low}`}>{r.severity}</span>
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">{r.detail}</div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
